import React, { ChangeEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Alert, Box, CircularProgress, FormControlLabel, Grid, Snackbar, Typography } from '@mui/material';
import {
  CheckboxStyled, FormContainer, GridContainer, Heading, IconContainer,
  ImageContainer, StyledButton, StyledTextField, TermsText
} from './SignIn.styles';
import BackGround from '../../../Assets/AuthBackGround/AuthBG.png';
import LinkedIn from "../../../Assets/AuthBackGround/LinkedIn.png";
import Facebook from "../../../Assets/AuthBackGround/facebook.png";
import GLogo from "../../../Assets/AuthBackGround/GLogo1.png";
import { theme } from '../../../theme';
import { getAccessToken } from './signApi';
import { checkUseridExists } from '../Admin/User/userApi';


interface FormValues {
  userid: string;
  password: string;
}

const SignIn: React.FC = () => {
  const [formValues, setFormValues] = useState<FormValues>({
    userid: '',
    password: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false)
  const { tenant } = useParams<{ tenant: string }>();




  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      navigate(`/${tenant}/Joblisting`);
    }
  }, [navigate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: ''
    }))
  };

  const handleUserIdBlur = async () => {
      const userId = formValues.userid.trim();
  
      if (userId === "") {
        setErrors(prev => ({
          ...prev,
          userid: 'User ID is required.',
        }));
        return false;
      }
  
      try {
        const result = await checkUseridExists(tenant, { UserID: userId ,CheckFor:"login"});
  
        if (!result) {
          setErrors(prev => ({
            ...prev,
            userid:'User ID not found. Please enter a valid one.',
          }));
          return false;
        } else {
          setErrors(prev => ({
            ...prev,
            userid: '',
          }));
          return true;
        }
      } catch (error) {
        console.error("User ID check failed:", error);
        setErrors(prev => ({
          ...prev,
          userid: 'Error validating User ID. Please try again later.',
        }));
        return false;
      }
    };

  const handleRememberMeChange = () => {
    setRememberMe(!rememberMe);
  };

  const handleSubmit = async () => {
    const newErrors: { [key: string]: string } = {};
    if (formValues.userid.trim().length === 0) {
      newErrors.userid = 'UserId is required.';
    }

    if (formValues.password.trim().length === 0) {
      newErrors.password = 'Password is required.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return
    }

    const validUserId = await handleUserIdBlur()

    if(!validUserId){
      return
    }
    setLoading(true)
    try {
      const payLoad = {
        userId: formValues.userid,
        password: formValues.password
      }
      const response = await getAccessToken(tenant, payLoad)
      if (response) {
        const { Status, Message, AccessToken } = response
        if (Status) {
          localStorage.setItem('access_token', AccessToken);
          sessionStorage.removeItem('candidate_session')
          navigate(`/${tenant}/Joblisting`); // Navigate to the dashboard page
        } else {
          // Backend responded with Status: False
          setErrors({ responseError: Message || 'Authentication failed. Please try again.' });
        }
      }
    } catch (error: any) {
      console.log(error)
    } finally {
      setLoading(false)
    }

  };

  // Handle key press event for Enter key
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };


  return (
    <Grid container className="sign-in-container" sx={{ height: '100vh', width: '100vw', overflow: 'hidden' }}>
      {/* Left side with image */}
      <Grid item xs={12} md={6} className="image-grid-item" sx={{ width: '50vw', height: '100vh' }}>
        <ImageContainer
          sx={{
            backgroundImage: `url(${BackGround})`,
            height: '100vh',
            width: '100%',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          className="image-container"
        />
      </Grid>

      {/* Right side with form */}
      <Grid item xs={12} md={6} className="form-grid-item" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <FormContainer className="form-container" sx={{ width: '40vw', padding: '2vh 2vw' }}>
          <Box className="group-one" sx={{ width: '100%', marginBottom: '3vh' }}>
            <Typography variant="h4" className="heading" >
              Sign In
            </Typography>
            <Typography variant="subtitle1" className="existing-account-text" sx={{ marginBottom: '2vh' }}>
              Sign in with an existing account
            </Typography>
          </Box>

          <Box className="group-two" sx={{ width: '100%' }}>
            {/* <IconContainer className="icon-container" sx={{ position: 'relative', marginBottom: '2vh' }}>
              <img src={LinkedIn} alt="LinkedIn Logo" width="42" height="42" className="social-icon" />
              <img src={Facebook} alt="Facebook Logo" width="42" height="42" className="social-icon" />
              <img src={GLogo} alt="Google Logo" width="42" height="42" className="social-icon" />
            </IconContainer> */}
            <GridContainer container spacing={3} className="grid-container" sx={{ pl: 3, alignItems: 'center' }}>
              {/* <StyledDivider className="styled-divider" sx={{ fontSize: '1.5vh', marginBottom: '2vh' }}>
              <Typography variant='body2'> or continue with Email</Typography>
            </StyledDivider> */}


              <StyledTextField
                label="User ID"
                name="userid"
                value={formValues.userid}
                onChange={handleChange}
                onBlur={handleUserIdBlur}
                fullWidth
                required
                size="small"
                className="styled-text-field"
                error={!!errors.userid}
                helperText={errors.userid}
                autoComplete='off'
                sx={{ fontSize: '1.8vh', mt: 2 }}
                onKeyDown={handleKeyPress}
                disabled={loading}
              />





              <StyledTextField
                label="Password"
                type="password"
                name="password"
                value={formValues.password}
                onChange={handleChange}
                fullWidth
                required
                size="small"
                className="styled-text-field"
                error={!!errors.password}
                helperText={errors.password}
                autoComplete='off'
                sx={{ fontSize: '1.8vh' }}
                onKeyDown={handleKeyPress}
                disabled={loading}
              />


              {/* Remember me and forgot password */}
              <GridContainer container spacing={2} className="grid-container" sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', paddingBottom: '1rem', margin: '10px 0 10px 0' }}>
                {/* <FormControlLabel
                control={<CheckboxStyled checked={rememberMe} onChange={handleRememberMeChange} size="small" />}
                label={<TermsText variant="body2" color={theme.palette.text.secondary} pr={2} className="terms-text" sx={{ fontSize: '1.5vh' }}>Remember me</TermsText>}
                sx={{ marginLeft: '0.1vw' }}
              /> */}
                <Link to={{ pathname: `/${tenant}/forgot-password`, }} state={{ userId: formValues.userid }} style={{ textDecoration: 'none' }}>
                  <Typography variant='body2' color='primary' mr={2} > Forgot password?</Typography>
                </Link>
              </GridContainer>

              {errors.responseError && (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 2 }}>
                  <Typography color="error" variant="body2" textAlign="center">
                    {errors.responseError}
                  </Typography>
                </Box>
              )}



              <StyledButton
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                fullWidth
                size="small"
                className="styled-button"
                sx={{ fontSize: '2vh', marginBottom: '1vh' }}
                disabled={loading}
              >
                {loading ? <CircularProgress size="30px" /> : "Sign In"}
              </StyledButton>

            </GridContainer>

            {/* Checkbox for terms and conditions */}
            {/* <Grid container spacing={2} alignItems="center" my={2}>
              <Grid item sx={{ml:'0.2vw'}}>
                <CheckboxStyled size="small" />
              </Grid>
              <Grid item>
                <Typography variant="body2" sx={{ fontSize: '1.5vh',position:'relative',left:'-0.5vw' }}>
                  By continuing, you accept our standard <Link to="/terms" style={{ textDecoration: 'none', color: '#3f51b5' }}>terms and conditions</Link> and <Link to="/privacy" style={{ textDecoration: 'none', color: '#3f51b5' }}>privacy policy</Link>.
                </Typography>
              </Grid>
            </Grid> */}
          </Box>

          {/* <Box mt={2} textAlign="left" className="sign-up-container">
            <Typography variant="body2" className="sign-up-text" sx={{ fontSize: '1.8vh' }}>
              Don't have an account?{' '}
              <Link to={`/${tenant}/SignUp`} className="sign-up-link" style={{ fontSize: '1.8vh',textDecoration:'none' }}>Sign Up</Link>
            </Typography>
          </Box> */}
        </FormContainer>
      </Grid>
    </Grid>
  );
};

export default SignIn;
